import React, { useEffect, useState } from 'react';
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import {
  PeopleAlt,
  Search,
  Person,
  Email,
  SupervisorAccount,
  Business,
  Wc,
  Phone,
  Edit,
  Delete,
} from "@mui/icons-material";
import { toast } from 'react-toastify';

import { createNewUser, deleteUser, getAllUsers, updateUser } from '../../Api/ApiUrlConst';
import { Button, Col, Container, Form, Input, Row } from 'reactstrap';
interface User {
  userID: string;
  name: string;
  email: string;
  role: string;
  department: string;
  gender: string;
  phoneNumber: string;
}

type SortKey = keyof User;

type SortConfig = {
  key: SortKey | null;
  direction: "asc" | "desc";
};

const AllUserComponents: React.FC<{ refreshTrigger: boolean }> = ({ refreshTrigger }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    department: '',
    gender: '',
    phoneNumber: '',
  });

  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await getAllUsers();
      setUsers(res.data?.$values || res.data || []); // Handle potential $values from API
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setModalOpen(true);
  };

  const confirmDelete = (id: string) => {
    setUserToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete);
      toast.success("User deleted");
      setUsers(prev => prev.filter(u => u.userID !== userToDelete));
    } catch {
      toast.error("Failed to delete user");
    } finally {
      setDeleteModalOpen(false);
      setUserToDelete(null);
    }
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    try {
      await updateUser(editingUser.userID, {
        name: editingUser.name,
        email: editingUser.email,
        role: editingUser.role,
        department: editingUser.department,
        gender: editingUser.gender,
        phoneNumber: editingUser.phoneNumber
      });
      toast.success("User updated");
      fetchUsers();
    } catch {
      toast.error("Update failed");
    } finally {
      setModalOpen(false);
      setEditingUser(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent page refresh
    try {
      await createNewUser(form);
      toast.success(`New ${form.role} user has been added.`);
      setForm({
        name: '',
        email: '',
        password: '',
        role: '',
        department: '',
        gender: '',
        phoneNumber: ''
      }); // Clear form
      fetchUsers(); // Refresh user list
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to create user");
    }
  };

  const handleSort = (key: SortKey) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig.key) return 0;

    const valA = a[sortConfig.key];
    const valB = b[sortConfig.key];

    const strA = valA?.toString().toLowerCase();
    const strB = valB?.toString().toLowerCase();

    if (strA < strB) return sortConfig.direction === "asc" ? -1 : 1;
    if (strA > strB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // Apply Filtering
  const filteredUsers = sortedUsers.filter((user) =>
    Object.values(user).some((value) =>
      value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <Container fluid className='mt-3'>
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" color="primary" fontWeight="bold">
            <PeopleAlt sx={{ mr: 1 }} /> User Management
          </Typography>
        </Box>
        <Form onSubmit={handleSubmit} className="responsive-row mb-2" >
          <Input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Name"
            required
          />
          <Input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="Email"
            required
          />
          <Input
            type="password"
            name="password"
            value={form.password}
            onChange={handleChange}
            placeholder="Password"
            required
          />
          <Input
            type="select"
            name="role"
            value={form.role}
            onChange={handleChange}
            required
          >
            <option value="">Role</option>
            <option value="Admin">Admin</option>
            <option value="ProjectManager">Project Manager</option>
            <option value="TeamLeader">Team Lead</option>
            <option value="TeamMember">Team Member</option>
                    <option value="Client">Client</option>

          </Input>
          <Input
            type="select"
            name="department"
            value={form.department}
            onChange={handleChange}
            required
          >
            <option value="">Department</option>
            <option value="IT">IT</option>
            <option value="Mechanical">Mechanical</option>
            <option value="Electrical">Electrical</option>
          </Input>
          <Input
            type="select"
            name="gender"
            value={form.gender}
            onChange={handleChange}
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </Input>
          <Input
            type="tel"
            name="phoneNumber"
            value={form.phoneNumber}
            onChange={handleChange}
            placeholder="Phone Number"
            pattern="^[0-9]{10,15}$"
            required
          />
          <Button
            type="submit"
            color="primary"
            size="sm"
          >
            Add
          </Button>
        </Form>




        {/* Search Box */}
        <Box display="flex" justifyContent="flex-end" mb={2} >
          {!showSearch ? (
            <IconButton onClick={() => setShowSearch(true)}>
              <Search />
            </IconButton>
          ) : (
            <TextField
              placeholder="Search..."
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onBlur={() => {
                if (!searchTerm) setShowSearch(false);
              }}
              autoFocus
            />
          )}
        </Box>

        {/* Table */}
        {loading ? (
          <Box textAlign="center" py={5}>
            <CircularProgress color="primary" />
            <Typography mt={2} color="text.secondary">
              Loading users...
            </Typography>
          </Box>
        ) : users.length === 0 ? (
          <Box textAlign="center" py={5}>
            <Typography variant="h6" color="text.secondary">
              No users found.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "primary.main" }}>
                <TableRow>
                  <TableCell sx={{ color: "white" }}>#</TableCell>
                  <TableCell onClick={() => handleSort("name")} sx={{ color: "white" }}>
                    <Person fontSize="small" /> Name
                  </TableCell>
                  <TableCell onClick={() => handleSort("email")} sx={{ color: "white" }}>
                    <Email fontSize="small" /> Email
                  </TableCell>
                  <TableCell onClick={() => handleSort("role")} sx={{ color: "white" }}>
                    <SupervisorAccount fontSize="small" /> Role
                  </TableCell>
                  <TableCell onClick={() => handleSort("department")} sx={{ color: "white" }}>
                    <Business fontSize="small" /> Dept
                  </TableCell>
                  <TableCell onClick={() => handleSort("gender")} sx={{ color: "white" }}>
                    <Wc fontSize="small" /> Gender
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>
                    <Phone fontSize="small" /> Phone
                  </TableCell>
                  <TableCell sx={{ color: "white" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user, index) => (
                  <TableRow key={user.userID}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{user.name}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{user.role}</TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>
                      {user.department}
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{user.gender}</TableCell>
                    <TableCell>{user.phoneNumber}</TableCell>
                    <TableCell>
                      <IconButton color="info" size="small" onClick={() => handleEdit(user)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => confirmDelete(user.userID)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box display="flex" flexDirection="column" gap={2} mt={1} sx={{
              width: '400px'
            }}>
              <TextField
                label="Full Name"
                value={editingUser.name}
                onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
              />
              <TextField
                label="Email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
              />
              <FormControl fullWidth variant="outlined" margin="normal">
                <InputLabel id="role-label">Role</InputLabel>
                <Select
                  labelId="role-label"
                  id="role-select"
                  value={editingUser.role || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="Admin">Admin</MenuItem>
                  <MenuItem value="ProjectManager">Project Manager</MenuItem>
                  <MenuItem value="Client">Client</MenuItem>
                  <MenuItem value="TeamLeader">Team Lead</MenuItem>
                  <MenuItem value="TeamMember">Team Member</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Department"
                value={editingUser.department}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, department: e.target.value })
                }
              />
              <TextField
                label="Gender"
                value={editingUser.gender}
                onChange={(e) => setEditingUser({ ...editingUser, gender: e.target.value })}
              />
              <TextField
                label="Phone"
                value={editingUser.phoneNumber}
                onChange={(e) =>
                  setEditingUser({ ...editingUser, phoneNumber: e.target.value })
                }
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUpdate} variant="contained" color="primary">
            Update
          </Button>
          <Button onClick={() => setModalOpen(false)} variant="outlined" color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            variant="outlined"
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AllUserComponents;